import React, {type ReactElement} from "react";
import {css} from "/src/utils/utils";
import * as styles from "./faq-accordion.module.scss";

import ModuleBody from "../../layouts/module-body/ModuleBody";
import Expandable from "../expandable/Expandable";
import MarkdownText from "../../markdown/markdown-text/MarkdownText";

type AccordionData = Omit<Queries.FaqFragmentFragment, "__typename">;

export interface AccordionProps {
  data: AccordionData;
  classes?: string;
}

/**
 * Component that displays a headline and an array of <Expandable> components.
 * Some visual styling, like padding and margins, have been intentionally left
 * out of this component because it is recognised that this component might be
 * used in different pages of the site. Styling therefore might differ according
 * to the page/section the component is used in.
 *
 * @param {AccordionProps} props
 * @param {Queries.FaqFragmentFragment} props.data
 * @param {string} classes
 */
const FAQAccordion = (props: AccordionProps): ReactElement => {
  /**
   * Extract props.
   */
  const {data, classes} = props;

  const {faqEyebrow, faqTitle, faqItems, anchorLinkId} = data;

  const expandableClasses = {
    icon: styles.faqAccordion__icon,
    item: styles.faqAccordion__item,
    itemOpened: styles.faqAccordion__itemOpened,
    title: styles.faqAccordion__title,
    titleClosed: styles.faqAccordion__titleClosed,
    titleOpened: styles.faqAccordion__titleOpened,
  };

  /**
   * Render.
   */
  return (
    <section {...(anchorLinkId ? {id: anchorLinkId} : {})}>
      <ModuleBody classes={css(styles.faqAccordion, classes!)}>
        {faqEyebrow && (
          <div className={css(styles.faqAccordion__eyebrow)}>
            <span
              className={css("eyebrow", styles.faqAccordion__eyebrow__content)}
            >
              {faqEyebrow}
            </span>
          </div>
        )}
        {faqTitle && <h2 className="h2">{faqTitle}</h2>}
        {faqItems &&
          faqItems.map((item, i) => {
            const key = `${item?.title! || "accordion-item"}-${i}`;

            return (
              <Expandable
                id={key}
                title={<h3 className="h3">{item?.title}</h3>}
                classes={expandableClasses}
                iconOpened={<CollapseIcon />}
                iconClosed={<ExpandIcon />}
                content={
                  <div className={css(styles.faqAccordion__content)}>
                    <div>
                      <MarkdownText text={item?.copy?.copy!} />
                    </div>
                  </div>
                }
                key={key}
              />
            );
          })}
      </ModuleBody>
    </section>
  );
};

/**
 * Internal component.
 */
const CollapseIcon = () => (
  <div
    data-state="collapsed"
    className={css(styles.faqAccordion__iconCollapse)}
  ></div>
);

/**
 * Internal component.
 */
const ExpandIcon = () => (
  <div
    data-state="expanded"
    className={css(styles.faqAccordion__iconExpand)}
  ></div>
);

export default FAQAccordion;
